import type { DawaSuggestion, RenderData, RenderFunction } from './types.ts';

export interface UiOptions {
	onSelect(item: DawaSuggestion): void;
	onTextChange(text: string, caretpos: number): void;
	render: RenderFunction | undefined;
	multiline: boolean;
}

export interface Ui {
	setSuggestions(suggestions: DawaSuggestion[]): void;
	/** Put `text` in the field with the caret at `caretpos` (default: the end) and close the list. */
	selectAndClose(text: string, caretpos?: number): void;
	destroy(): void;
}

let instances = 0;

/**
 * The default list: `ul.dawa-autocomplete-suggestions > li.dawa-autocomplete-suggestion`, the
 * highlighted one with `.dawa-selected` — the markup existing stylesheets were written for.
 */
export const defaultRender: RenderFunction = (container, data, onSelect, multiline) => {
	const doc = container.ownerDocument;
	container.textContent = '';
	if (!data.focused || data.suggestions.length === 0) return;
	const listId = container.dataset.listId ?? '';
	const ul = doc.createElement('ul');
	ul.className = 'dawa-autocomplete-suggestions';
	ul.setAttribute('role', 'listbox');
	if (listId) ul.id = listId;
	data.suggestions.forEach((s, i) => {
		const li = doc.createElement('li');
		li.className =
			'dawa-autocomplete-suggestion' + (i === data.selected ? ' dawa-selected' : '');
		li.setAttribute('role', 'option');
		li.setAttribute('aria-selected', String(i === data.selected));
		if (listId) li.id = `${listId}-${i}`;
		// mousedown, not click: a click lands after the input's blur has closed the list.
		li.addEventListener('mousedown', (e) => {
			e.preventDefault();
			onSelect(i);
		});
		// Keep "Jægersborggade 10" on one line inside a narrow list.
		const rows = s.forslagstekst.split('\n').map((r) => r.replace(/ /g, ' '));
		if (multiline) {
			rows.forEach((row, r) => {
				if (r > 0) li.appendChild(doc.createElement('br'));
				li.appendChild(doc.createTextNode(row));
			});
		} else {
			li.textContent = rows.join(', ');
		}
		ul.appendChild(li);
	});
	container.appendChild(ul);
};

/** The suggestion list under one input field, and the keyboard and mouse handling around it. */
export function autocompleteUi(input: HTMLInputElement, o: UiOptions): Ui {
	const doc = input.ownerDocument;
	const render = o.render ?? defaultRender;
	const container = doc.createElement('div');
	const listId = `dawa-autocomplete-${++instances}`;
	container.dataset.listId = listId;
	input.parentNode?.insertBefore(container, input.nextSibling);

	const data: RenderData = {
		caretpos: 2,
		inputText: '',
		selected: 0,
		focused: doc.activeElement === input,
		suggestions: [],
	};
	let lastText = '';
	let lastCaret = 0;
	let destroyed = false;

	const draw = (): void => {
		if (destroyed) return;
		render(container, data, choose, o.multiline);
		const open = data.focused && data.suggestions.length > 0;
		input.setAttribute('aria-expanded', String(open));
		if (open) input.setAttribute('aria-activedescendant', `${listId}-${data.selected}`);
		else input.removeAttribute('aria-activedescendant');
	};

	const writeInput = (text: string, caretpos: number): void => {
		input.value = text;
		lastText = text;
		lastCaret = caretpos;
		data.inputText = text;
		data.caretpos = caretpos;
		if (doc.activeElement === input) {
			try {
				input.setSelectionRange(caretpos, caretpos);
			} catch {
				// type="email" and friends have no selection API; the text is what matters.
			}
		}
	};

	function choose(index: number): void {
		const item = data.suggestions[index];
		if (!item) return;
		writeInput(item.tekst, item.caretpos);
		data.suggestions = [];
		draw();
		o.onSelect(item);
	}

	const changed = (): void => {
		const text = input.value;
		const caret = input.selectionStart ?? text.length;
		data.inputText = text;
		data.caretpos = caret;
		if (text === lastText && caret === lastCaret) return;
		lastText = text;
		lastCaret = caret;
		o.onTextChange(text, caret);
	};

	const onKeydown = (e: KeyboardEvent): void => {
		if (!data.focused || data.suggestions.length === 0) return;
		const n = data.suggestions.length;
		if (e.key === 'ArrowDown' || e.key === 'Down') {
			data.selected = (data.selected + 1) % n;
			draw();
		} else if (e.key === 'ArrowUp' || e.key === 'Up') {
			data.selected = (data.selected - 1 + n) % n;
			draw();
		} else if (e.key === 'Enter' || e.key === 'Tab') {
			choose(data.selected);
		} else if (e.key === 'Escape' || e.key === 'Esc') {
			data.suggestions = [];
			draw();
		} else {
			return;
		}
		e.preventDefault();
	};
	const onFocus = (): void => {
		data.focused = true;
		draw();
	};
	const onBlur = (): void => {
		data.focused = false;
		draw();
	};

	input.addEventListener('keydown', onKeydown);
	input.addEventListener('focus', onFocus);
	input.addEventListener('blur', onBlur);
	input.addEventListener('input', changed);
	input.addEventListener('mouseup', changed);
	input.setAttribute('autocomplete', 'off');
	input.setAttribute('role', 'combobox');
	input.setAttribute('aria-autocomplete', 'list');
	input.setAttribute('aria-controls', listId);
	draw();

	return {
		setSuggestions(suggestions) {
			data.suggestions = suggestions;
			data.selected = 0;
			draw();
		},
		selectAndClose(text, caretpos = text.length) {
			writeInput(text, caretpos);
			data.suggestions = [];
			data.selected = 0;
			draw();
		},
		destroy() {
			destroyed = true;
			input.removeEventListener('keydown', onKeydown);
			input.removeEventListener('focus', onFocus);
			input.removeEventListener('blur', onBlur);
			input.removeEventListener('input', changed);
			input.removeEventListener('mouseup', changed);
			for (const a of ['role', 'aria-autocomplete', 'aria-controls', 'aria-expanded']) {
				input.removeAttribute(a);
			}
			input.removeAttribute('aria-activedescendant');
			container.remove();
		},
	};
}
